/**
 * Ported from clario-frontend src/utils/theme/sidebarTheme.ts.
 * Divergence: the source resolves light-mode active colors through the
 * user-palette CSS vars (`--indigo-500`); the ecosystem has no runtime
 * palette yet, so the default indigo-500 (#6366F1) literals are used.
 */

export type SidebarThemeDerived = {
  railBg: string;
  iconColor: string;
  hoverBg: string;
  activeBg: string;
  activeIconColor: string;
  borderColor: string;
};

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '');
  if (clean.length === 3) {
    const r = parseInt(clean[0]! + clean[0]!, 16);
    const g = parseInt(clean[1]! + clean[1]!, 16);
    const b = parseInt(clean[2]! + clean[2]!, 16);
    return { r, g, b };
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function toHex2(value: number): string {
  return Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, '0');
}

/** Blend a hex color toward white or black by `amount` (0 = no change, 1 = full). */
export function blendHex(hex: string, toward: 'white' | 'black', amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const target = toward === 'white' ? 255 : 0;
  const r = rgb.r + (target - rgb.r) * amount;
  const g = rgb.g + (target - rgb.g) * amount;
  const b = rgb.b + (target - rgb.b) * amount;
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toUpperCase();
}

/** WCAG relative luminance (0 = black, 1 = white). */
function getLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/** Returns true if the color is considered dark (icon should be white). */
export function isDarkColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return getLuminance(rgb.r, rgb.g, rgb.b) < 0.35;
}

/**
 * Given a rail background color, compute all derived sidebar CSS values.
 * Only the railColor needs to be stored — everything else is calculated.
 */
export function deriveSidebarTheme(railColor: string): SidebarThemeDerived {
  const dark = isDarkColor(railColor);
  return {
    railBg: railColor,
    iconColor: dark ? '#FFFFFF' : '#1F2937',
    hoverBg: blendHex(railColor, dark ? 'white' : 'black', 0.12),
    activeBg: dark ? blendHex(railColor, 'white', 0.22) : 'rgb(99 102 241 / 0.12)',
    activeIconColor: dark ? '#FFFFFF' : '#6366F1',
    borderColor: blendHex(railColor, 'black', 0.1),
  };
}
